import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Group, Instance, Person, SessionPlan } from '@gm-binder/shared';
import { api, mediaUrl } from '../api/client';
import { AppShell } from '../components/layout/AppShell';
import {
  PageHeader,
  Card,
  Badge,
  Button,
  MediaImage,
  LoadingState,
  EmptyState,
} from '../components/ui';
import { formatDate, formatSlot } from '../lib/datetime';
import { groupAccent, initials, sessionsLabel } from '../lib/groups';
import styles from './TablePage.module.css';

function confirmedStart(plan: SessionPlan): string | null {
  if (!plan.confirmedSlotId) return null;
  return plan.slots.find((s) => s.id === plan.confirmedSlotId)?.start ?? null;
}

function lastPlayed(inst: Instance): string | null {
  if (!inst.sessionLog.length) return null;
  return inst.sessionLog.map((e) => e.date).sort((a, b) => b.localeCompare(a))[0];
}

export function TablePage() {
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState<Person[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [plans, setPlans] = useState<SessionPlan[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const [ppl, grps, summaries, allPlans] = await Promise.all([
          api.listPeople(),
          api.listGroups(),
          api.listInstances(),
          api.listSessionPlans(),
        ]);
        const active = summaries.filter((s) => s.status !== 'completed');
        const full = await Promise.all(active.map((s) => api.getInstance(s.id)));
        setPeople(ppl);
        setGroups(grps);
        setInstances(full.filter((i): i is Instance => !!i));
        setPlans(allPlans);
      } catch {
        /* leave empty */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const personById = useMemo(() => new Map(people.map((p) => [p.id, p] as const)), [people]);
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g] as const)), [groups]);
  const instanceById = useMemo(
    () => new Map(instances.map((i) => [i.id, i] as const)),
    [instances],
  );

  const upcoming = useMemo(() => {
    const now = Date.now();
    return plans
      .map((p) => ({ plan: p, start: confirmedStart(p) }))
      .filter((x): x is { plan: SessionPlan; start: string } => !!x.start)
      .filter((x) => new Date(x.start).getTime() >= now - 12 * 3600 * 1000)
      .sort((a, b) => a.start.localeCompare(b.start));
  }, [plans]);

  const openPolls = useMemo(
    () => plans.filter((p) => p.status === 'open' && p.slots.length > 0),
    [plans],
  );

  const activeRuns = useMemo(
    () =>
      [...instances]
        .filter((i) => i.status === 'active')
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [instances],
  );
  const pausedRuns = useMemo(() => instances.filter((i) => i.status === 'paused'), [instances]);

  if (loading) {
    return (
      <AppShell>
        <LoadingState label="Apparecchio il tavolo…" />
      </AppShell>
    );
  }

  const isFreshStart = instances.length === 0 && groups.length === 0 && plans.length === 0;

  // The focal centerpiece: the next confirmed session, else the freshest active run.
  const heroPlan = upcoming[0] ?? null;
  const heroInst = heroPlan
    ? instanceById.get(heroPlan.plan.instanceId) ?? null
    : activeRuns[0] ?? null;
  const heroGroup = heroInst?.groupId ? groupById.get(heroInst.groupId) ?? null : null;

  const resumeRuns = [...activeRuns, ...pausedRuns].filter((i) => i.id !== heroInst?.id);

  return (
    <AppShell>
      <PageHeader
        eyebrow="Il tuo tavolo"
        title="Il Tavolo"
        subtitle="Cosa ti aspetta, e dove eri rimasto."
        actions={
          <Link to="/groups">
            <Button icon="👥">I gruppi</Button>
          </Link>
        }
      />

      {isFreshStart ? (
        <EmptyState
          icon="🕯️"
          title="Il tavolo è ancora apparecchiato al buio"
          description="Crea un gruppo di giocatori e accendi la prima candela: da lì potrai avviare una one-shot e pianificare la serata."
          actions={
            <>
              <Link to="/groups">
                <Button variant="primary">Crea un gruppo</Button>
              </Link>
              <Link to="/library">
                <Button variant="ghost">Sfoglia la libreria</Button>
              </Link>
            </>
          }
        />
      ) : (
        <>
          {/* Focal centerpiece */}
          {heroInst && (
            <section
              className={styles.hero}
              style={heroGroup ? { ['--accent' as string]: groupAccent(heroGroup.color) } : undefined}
            >
              <div className={styles.heroArt}>
                <MediaImage
                  src={mediaUrl(heroInst.adventureSlug, heroInst.adventureCover)}
                  alt=""
                  fallback="🕯️"
                  className={styles.heroCover}
                />
                <span className={styles.heroScrim} aria-hidden />
              </div>
              <div className={styles.heroBody}>
                <span className={styles.heroEyebrow}>
                  {heroPlan ? 'Prossima sessione' : 'Riprendi dove eri'}
                </span>
                <h2 className={styles.heroTitle}>{heroInst.name}</h2>
                <div className={styles.heroMeta}>
                  {heroGroup && (
                    <Link to={`/groups/${heroGroup.id}`} className={styles.heroGroup}>
                      <span className={styles.seal} aria-hidden />
                      {heroGroup.name}
                    </Link>
                  )}
                  <span className={styles.heroAdv}>{heroInst.adventureTitle}</span>
                </div>

                {heroPlan ? (
                  <div className={styles.heroWhen}>
                    <span className={styles.heroDate}>{formatSlot(heroPlan.start)}</span>
                    <span className={styles.heroDot}>·</span>
                    <span>
                      {
                        heroPlan.plan.responses.filter((r) =>
                          r.slots.some(
                            (s) =>
                              s.slotId === heroPlan.plan.confirmedSlotId && s.availability === 'yes',
                          ),
                        ).length
                      }{' '}
                      al tavolo
                    </span>
                    {heroPlan.plan.location && (
                      <>
                        <span className={styles.heroDot}>·</span>
                        <span>📍 {heroPlan.plan.location}</span>
                      </>
                    )}
                  </div>
                ) : (
                  <div className={styles.heroWhen}>
                    <Badge tone="success">In corso</Badge>
                    {lastPlayed(heroInst) && (
                      <span>Ultima: {formatDate(lastPlayed(heroInst))}</span>
                    )}
                    <span>
                      {heroInst.sessionLog.length === 1
                        ? '1 sessione giocata'
                        : `${heroInst.sessionLog.length} sessioni giocate`}
                    </span>
                  </div>
                )}

                <div className={styles.heroActions}>
                  <Link to={`/run/${heroInst.id}`}>
                    <Button variant="primary" size="lg" icon="🕯️">
                      {heroPlan ? 'Apri la sessione' : 'Riprendi'}
                    </Button>
                  </Link>
                  {heroGroup && (
                    <Link to={`/groups/${heroGroup.id}`}>
                      <Button variant="ghost">Vai al gruppo</Button>
                    </Link>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Resume — other live/paused games */}
          {resumeRuns.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Al tavolo</h3>
              <div className={styles.list}>
                {resumeRuns.map((inst) => {
                  const g = inst.groupId ? groupById.get(inst.groupId) : null;
                  const last = lastPlayed(inst);
                  return (
                    <Card key={inst.id} padded={false}>
                      <div className={styles.row}>
                        <MediaImage
                          src={mediaUrl(inst.adventureSlug, inst.adventureCover)}
                          alt=""
                          fallback="🕯️"
                          className={styles.cover}
                        />
                        <div className={styles.rowMain}>
                          <div className={styles.name}>{inst.name}</div>
                          <div className={styles.meta}>
                            <Badge tone={inst.status === 'active' ? 'success' : 'warn'}>
                              {inst.status === 'active' ? 'In corso' : 'In pausa'}
                            </Badge>
                            {g && (
                              <Link to={`/groups/${g.id}`} className={styles.groupChip}>
                                <span
                                  className={styles.seal}
                                  style={{ ['--accent' as string]: groupAccent(g.color) }}
                                  aria-hidden
                                />
                                {g.name}
                              </Link>
                            )}
                            {last && <span>Ultima: {formatDate(last)}</span>}
                          </div>
                        </div>
                        <Link to={`/run/${inst.id}`}>
                          <Button size="sm" variant="primary">
                            Riprendi
                          </Button>
                        </Link>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {/* Open polls awaiting responses */}
          {openPolls.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>In attesa di risposte</h3>
              <div className={styles.list}>
                {openPolls.map((plan) => {
                  const inst = instanceById.get(plan.instanceId);
                  return (
                    <Card key={plan.id} padded={false}>
                      <div className={styles.row}>
                        <span className={styles.pollIcon} aria-hidden>
                          🗳️
                        </span>
                        <div className={styles.rowMain}>
                          <div className={styles.name}>{inst?.name ?? plan.title}</div>
                          <div className={styles.meta}>
                            <span>{plan.slots.length} date proposte</span>
                            <span>{plan.responses.length} risposte</span>
                          </div>
                        </div>
                        {inst && (
                          <Link to={`/run/${inst.id}`}>
                            <Button size="sm" variant="ghost">
                              Gestisci
                            </Button>
                          </Link>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {/* Group lanes */}
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h3 className={styles.sectionTitle}>I tuoi gruppi</h3>
              <Link to="/groups" className={styles.seeAll}>
                Tutti i gruppi →
              </Link>
            </div>
            <div className={styles.groupLanes}>
              {groups.map((g) => {
                const members = g.memberIds
                  .map((id) => personById.get(id))
                  .filter((p): p is Person => !!p);
                const sessions = instances.filter((i) => i.groupId === g.id).length;
                return (
                  <Link
                    key={g.id}
                    to={`/groups/${g.id}`}
                    className={styles.groupCard}
                    style={{ ['--accent' as string]: groupAccent(g.color) }}
                  >
                    <span className={styles.groupSeal} aria-hidden />
                    <span className={styles.groupName}>{g.name}</span>
                    <span className={styles.avatars}>
                      {members.slice(0, 5).map((p) => (
                        <span key={p.id} className={styles.avatar} title={p.name}>
                          {initials(p.name)}
                        </span>
                      ))}
                      {members.length === 0 && (
                        <span className={styles.groupHint}>Nessun membro</span>
                      )}
                    </span>
                    <span className={styles.groupStat}>
                      {sessions ? sessionsLabel(sessions) : 'Nessuna sessione'}
                    </span>
                  </Link>
                );
              })}
              <Link to="/groups" className={styles.groupAdd}>
                <span className={styles.groupAddPlus} aria-hidden>
                  ＋
                </span>
                Nuovo gruppo
              </Link>
            </div>
          </section>
        </>
      )}
    </AppShell>
  );
}
